import React, { useMemo } from "react";
import {
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    AccountingReportCycleTotal,
} from "@common/constants";
import { resolveEffectiveReportCycle } from "@common/helpers/reports";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import styles from "./AccountingReportFiltersForm.module.scss";

const cycleLabels: Record<AccountingReportCycleOptionType, string> = {
    [AccountingReportCycle.MONTH]: "By month",
    [AccountingReportCycle.QUARTER]: "By quarter",
    [AccountingReportCycle.YEAR]: "By year",
    [AccountingReportCycle.WEEK]: "By week",
    [AccountingReportCycle.ENTITY]: "By entity",
    [AccountingReportCycle.CLASS]: "By class",
    [AccountingReportCycle.SUBCLASS]: "By subclass",
    [AccountingReportCycleTotal.TOTAL]: "Total only",
};

const periodOptions: AccountingReportCycleOptionType[] = [
    AccountingReportCycleTotal.TOTAL,
    AccountingReportCycle.WEEK,
    AccountingReportCycle.MONTH,
    AccountingReportCycle.QUARTER,
    AccountingReportCycle.YEAR,
];

const toCycleItem = (
    option: AccountingReportCycleOptionType,
): PickerItemData<AccountingReportCycleOptionType> => ({
    value: option,
    label: cycleLabels[option],
});

interface CycleSelectProps {
    value?: AccountingReportCycleOptionType;
    onChange: (cycle: AccountingReportCycleOptionType) => void;
    showEntityOption?: boolean;
    showClassOption?: boolean;
    disabled?: boolean;
    className?: string;
    id?: string;
}

export const AccountingReportCycleSelect: React.FC<CycleSelectProps> = ({
    value,
    onChange,
    showEntityOption = false,
    showClassOption = false,
    disabled = false,
    className,
    id,
}) => {
    const segmentOptions = useMemo(
        () => [
            ...(showEntityOption ? [AccountingReportCycle.ENTITY] : []),
            ...(showClassOption ? [AccountingReportCycle.CLASS] : []),
        ],
        [showEntityOption, showClassOption],
    );

    const selectedValue = resolveEffectiveReportCycle({
        cycle: value ?? AccountingReportCycleTotal.TOTAL,
    });

    const listProps = useMemo(() => {
        const periodItems = periodOptions.map(toCycleItem);
        if (segmentOptions.length === 0) {
            return { items: periodItems };
        }
        return {
            sections: [
                { title: "Period", items: periodItems },
                { title: "Columns", items: segmentOptions.map(toCycleItem) },
            ],
        };
    }, [segmentOptions]);

    return (
        <Picker.Select
            {...listProps}
            id={id}
            value={selectedValue}
            onValueChange={(cycle) =>
                onChange(cycle ?? AccountingReportCycleTotal.TOTAL)
            }
            disabled={disabled}
            renderValue={() => cycleLabels[selectedValue]}
            trigger={
                <Picker.Button
                    variant="primary"
                    size="md"
                    className={className ?? styles.cycleSelect}
                />
            }
        />
    );
};
